import {
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  Button,
  Checkbox,
  ListItem,
  ListItemText,
  Autocomplete as MuiAutocomplete,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { type FormControlLabelProps } from '@mui/material/FormControlLabel'

import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { useAutocompleteManageProps } from './hooks/useAutocompleteManageProps'
import { usePartialListManageProps } from './hooks/usePartialListManageProps'
import { useSplitAndSortTagByType } from './hooks/useSplitAndSortTagByType'
import { useTexts } from './hooks/useTexts'

import { SELECT_OPTION } from './constants'
import { AutocompleteOption, BulkTagsFieldValue } from './types'

import { Footer } from './components/Footer'
import { PartialList } from './components/PartialList'

export type BulkTagsFieldProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkTagsFieldValue,
> = {
  formControlLabelProps?: Partial<FormControlLabelProps>
  options: AutocompleteOption[]
  bulkTagsProps: {
    bannedTagCategory: 'tagsIn' | 'tagsOut'
    totalSelectedItems: number
  }
} & TidalFormBridgeOptions<BulkTagsFieldValue, TargetField, State, AcceptedFormValue>

export function BulkTagsField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkTagsFieldValue,
>(props: BulkTagsFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    label,
    helperText,
    options,
    validateOn = 'change',
    testId,
    disabled = false,
    bulkTagsProps,
    ...rest
  } = props
  const { bannedTagCategory, totalSelectedItems } = bulkTagsProps

  const {
    fieldInfo: { onChange, value, errorOrHelperText, fieldHasError },
  } = useTidalFormBridge<BulkTagsFieldValue, TargetField, State, AcceptedFormValue>({
    validateOn,
    ...rest,
  })

  const { exact, partial } = useSplitAndSortTagByType(value)

  const defaultProps = {
    options,
    freeSolo: false,
    multiple: true,
    disabled,
    disableCloseOnSelect: true,
    componentsProps: {
      popper: {
        sx: {
          '& .MuiAutocomplete-listbox': {
            padding: 0,
          },
        },
      },
    },
  }

  const texts = useTexts()

  const {
    inputValue,
    selectedOptions,
    isOptionEqualToValue,
    getOptionLabel,
    getOptionDisabled,
    filterOptions,
    onChangeValue,
    onChangeInput,
    onClearInput,
    onSelectDeselectAllOptions,
  } = useAutocompleteManageProps(onChange, value, exact, totalSelectedItems)

  const { onAddTag, onDeleteTag } = usePartialListManageProps(onChange, value)

  return (
    <Stack direction="column" spacing={1}>
      <MuiAutocomplete
        {...defaultProps}
        value={selectedOptions}
        getOptionLabel={getOptionLabel}
        getOptionDisabled={getOptionDisabled}
        onChange={onChangeValue}
        inputValue={inputValue}
        onInputChange={onChangeInput}
        isOptionEqualToValue={isOptionEqualToValue}
        filterOptions={filterOptions}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            label={label}
            placeholder={texts.placeholder}
            error={fieldHasError}
            helperText={errorOrHelperText || helperText}
            data-testid={testId}
            fullWidth
          />
        )}
        renderOption={(props, option: AutocompleteOption, state: AutocompleteRenderOptionState) => {
          const isSecondaryactionRequired = option.count !== undefined && option.count > 0
          if (option.type === SELECT_OPTION.type) {
            const searchValue = inputValue?.trim().toLowerCase() || ''
            //Filter options by input value
            const filteredOptionsBySearchValue = options.filter(option =>
              option.label.toLowerCase().includes(searchValue),
            )
            //Remove disabled options
            const selectableFilteredOptions = filteredOptionsBySearchValue.filter(
              option => !option.warning,
            )
            //Check if all selectable options are selected
            const allOptionsSelected = selectableFilteredOptions.every(option =>
              selectedOptions.some(selected => selected.value === option.value),
            )
            //Check if there are any selectable options
            const isOptionsAvailable = selectableFilteredOptions.length > 0

            return (
              <>
                {isOptionsAvailable ? (
                  <Footer>
                    <Button
                      fullWidth
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                      }}
                      onClick={e => {
                        e.stopPropagation()
                        onSelectDeselectAllOptions(allOptionsSelected, selectableFilteredOptions)
                      }}
                    >
                      {allOptionsSelected
                        ? texts.deselectAllTags(selectableFilteredOptions.length)
                        : texts.selectAllTags(selectableFilteredOptions.length)}
                    </Button>
                  </Footer>
                ) : (
                  <>
                    <ListItem sx={{ height: '50px', marginRight: '40px' }}>
                      <ListItemText>{texts.emptyTags}</ListItemText>
                    </ListItem>
                    <Footer>
                      <Button
                        fullWidth
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                        }}
                        onClick={e => {
                          e.stopPropagation()
                          onClearInput()
                        }}
                      >
                        {texts.clearSearch}
                      </Button>
                    </Footer>
                  </>
                )}
              </>
            )
          }

          return (
            <Tooltip
              key={state.index}
              title={option.warning ? texts.disabledTooltip : null}
              placement="bottom"
            >
              <span>
                <ListItem
                  {...props}
                  disablePadding
                  disableGutters
                  sx={{ height: '50px' }}
                  secondaryAction={
                    isSecondaryactionRequired ? (
                      <Typography variant="caption">{`${option.count}/${option.allocationCount}`}</Typography>
                    ) : null
                  }
                >
                  <Checkbox checked={state.selected} />
                  <ListItemText>{option.label}</ListItemText>
                </ListItem>
              </span>
            </Tooltip>
          )
        }}
      />
      <PartialList
        tags={partial}
        onAddTag={onAddTag}
        onDeleteTag={onDeleteTag}
        totalSelectedItems={totalSelectedItems}
        bannedTagCategory={bannedTagCategory}
      />
    </Stack>
  )
}
